import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { PerformanceAnswer, ReviewAnswerEvaluationFragment } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import i18n from 'modules/core/lib/i18n'

import { BoxedCallout } from 'modules/performance_process/components/common/BoxedCallout'
import { WeightTag } from 'modules/performance_process/components/score_configuration_weights/WeightTag'
import { useStrategyWeights } from 'modules/performance_process/hooks/useStrategiesScoreConfiguration'
import { mapQuestionnaireResponse } from 'modules/performance_process/lib/questionnaireForStrategies'
import { QuestionnaireResponse } from 'modules/questionnaires/components/QuestionnaireResponse'

import CompetencyRatingQuestion from './CompetencyRatingQuestion'
import { RatingAverageScore } from './RatingAverageScore'
import * as styles from './index.vanilla.css'

export const EvaluationAnswer: React.FC<{
  evaluation: ReviewAnswerEvaluationFragment
  ratingAverageInEvaluationFeatureEnabled: boolean
  onUpdateAnswer: (updatedAnswer: PerformanceAnswer) => void
  hasAttemptedSubmit?: boolean
  readOnly: boolean
}> = ({
  evaluation,
  ratingAverageInEvaluationFeatureEnabled,
  onUpdateAnswer,
  hasAttemptedSubmit = false,
  readOnly,
}) => {
  const type = evaluation.evaluationType

  const { isSectionWeightVisible, weightsBySectionId } = useStrategyWeights({
    strategy: type,
    scoreConfiguration: evaluation.performanceReviewProcess?.scoreConfiguration,
  })

  const canSeeCustomWeights = evaluation.performanceReviewProcess?._policies?.manage?.can ?? false

  const answeredQuestionnaire = mapQuestionnaireResponse(
    evaluation.answeredQuestionnaires?.answeredQuestionnaireWithSections
  )

  const answeredEmployeeScoreQuestionnaire = mapQuestionnaireResponse(
    evaluation.answeredQuestionnaires?.answeredEmployeeScoreQuestionnaire
  )

  const answeredEmployeePotentialScoreQuestionnaire = mapQuestionnaireResponse(
    evaluation.answeredQuestionnaires?.answeredEmployeePotentialScoreQuestionnaire
  )

  const answeredCompetenciesQuestionnaire = mapQuestionnaireResponse(
    evaluation.answeredQuestionnaires?.answeredCompetenciesQuestionnaire
  )

  const renderSectionHeader = useUnsafeCallback(
    (sectionId: string) => {
      if (isSectionWeightVisible && canSeeCustomWeights) {
        return <WeightTag weight={weightsBySectionId[sectionId] ?? 0} weightable />
      }
    },
    [weightsBySectionId, isSectionWeightVisible, canSeeCustomWeights]
  )

  return (
    <Box className={styles.content}>
      <Box gap="s32" gridColSpan={{ xxs: 10 }}>
        <Text weight="semibold" size="400" color="grey1000">
          {i18n.t('performance_process.reviews.answer_evaluation.questionnaire')}
        </Text>
        <QuestionnaireResponse
          readonly={readOnly}
          content={answeredQuestionnaire}
          emptyState={
            answeredEmployeeScoreQuestionnaire.length ||
            answeredCompetenciesQuestionnaire.length ||
            answeredEmployeePotentialScoreQuestionnaire.length ? (
              <></>
            ) : undefined
          }
          onAnswer={onUpdateAnswer}
          renderSectionHeaderAddon={renderSectionHeader}
          hasAttemptedSubmit={hasAttemptedSubmit}
        />

        <QuestionnaireResponse
          readonly={readOnly}
          content={answeredCompetenciesQuestionnaire}
          emptyState={<></>}
          onAnswer={onUpdateAnswer}
          renderSectionHeaderAddon={renderSectionHeader}
          hasAttemptedSubmit={hasAttemptedSubmit}
          renderCustomQuestion={CompetencyRatingQuestion}
        />

        <QuestionnaireResponse
          readonly={readOnly}
          content={answeredEmployeeScoreQuestionnaire}
          emptyState={<></>}
          onAnswer={onUpdateAnswer}
          renderSectionHeaderAddon={renderSectionHeader}
          hasAttemptedSubmit={hasAttemptedSubmit}
        />

        <QuestionnaireResponse
          readonly={readOnly}
          content={answeredEmployeePotentialScoreQuestionnaire}
          emptyState={<></>}
          onAnswer={onUpdateAnswer}
          hasAttemptedSubmit={hasAttemptedSubmit}
          renderSectionFooter={() => (
            <BoxedCallout
              description={i18n.t(
                'performance_process.employee_potential_score.questionnaire.callout'
              )}
            />
          )}
        />

        {ratingAverageInEvaluationFeatureEnabled && (
          <RatingAverageScore questionnaire={answeredQuestionnaire} />
        )}
      </Box>
    </Box>
  )
}
